import type { CalculatorInput, CalculatorOutput, YearSeriesPoint } from './types';

function validatePositive(value: number, field: string): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(`${field} must be a non-negative number`);
  }
}

export function computeAnnualUplift(currentSalary: number, targetSalary: number, growthRate: number, year: number): number {
  const baseUplift = Math.max(targetSalary - currentSalary, 0);
  const growthFactor = Math.pow(1 + growthRate, year - 1);
  return baseUplift * growthFactor;
}

function buildSeries(input: CalculatorInput, totalYears: number): YearSeriesPoint[] {
  const series: YearSeriesPoint[] = [];
  let cumulativeUplift = 0;
  const growthRate = input.annual_growth_rate / 100;
  const netCost = Math.max(input.tuition_cost - input.employer_reimbursement, 0);

  for (let year = 1; year <= totalYears; year += 1) {
    const annualUplift = computeAnnualUplift(input.current_salary, input.target_salary, growthRate, year);
    cumulativeUplift += annualUplift;

    series.push({
      year,
      annual_uplift: annualUplift,
      cumulative_uplift: cumulativeUplift,
      net_after_tuition: cumulativeUplift - netCost
    });
  }

  return series;
}

function computeBreakEven(series: YearSeriesPoint[], netCost: number): { year: number | null; month: number | null } {
  if (netCost === 0) {
    return { year: 0, month: 0 };
  }

  const crossing = series.find((point) => point.net_after_tuition >= 0);
  if (!crossing) {
    return { year: null, month: null };
  }

  if (crossing.year === 1) {
    const ratio = netCost / Math.max(crossing.annual_uplift, 1);
    const month = Math.max(1, Math.min(12, Math.ceil(ratio * 12)));
    return { year: 1, month };
  }

  const prev = series[crossing.year - 2];
  const needed = netCost - prev.cumulative_uplift;
  const yearFraction = needed / Math.max(crossing.annual_uplift, 1);
  const month = Math.max(1, Math.min(12, Math.ceil(yearFraction * 12)));

  return { year: crossing.year, month };
}

export function calculateRoi(input: CalculatorInput): CalculatorOutput {
  validatePositive(input.current_salary, 'current_salary');
  validatePositive(input.target_salary, 'target_salary');
  validatePositive(input.employer_reimbursement, 'employer_reimbursement');
  validatePositive(input.time_horizon_years, 'time_horizon_years');
  validatePositive(input.annual_growth_rate, 'annual_growth_rate');
  validatePositive(input.tuition_cost, 'tuition_cost');

  if (input.current_age < 18 || input.current_age > 90) {
    throw new Error('current_age must be between 18 and 90');
  }
  if (input.retirement_age < input.current_age) {
    throw new Error('retirement_age must be >= current_age');
  }

  const netCost = Math.max(input.tuition_cost - input.employer_reimbursement, 0);
  const lifetimeYears = Math.max(input.retirement_age - input.current_age, 0);
  const totalSeriesYears = Math.max(input.time_horizon_years, lifetimeYears, 5);
  const series = buildSeries(input, totalSeriesYears);

  const fiveYearGain = series.slice(0, 5).reduce((sum, point) => sum + point.annual_uplift, 0);
  const roi5yNet = fiveYearGain - netCost;
  const roi5y = netCost > 0 ? roi5yNet / netCost : null;

  const lifetimeGain = series.slice(0, lifetimeYears).reduce((sum, point) => sum + point.annual_uplift, 0);
  const lifetimeDelta = lifetimeGain - netCost;

  const breakEven = computeBreakEven(series, netCost);

  return {
    break_even_year: breakEven.year,
    break_even_month: breakEven.month,
    roi_5y: roi5y,
    roi_5y_net: roi5yNet,
    lifetime_delta: lifetimeDelta,
    net_cost: netCost,
    series: series.slice(0, input.time_horizon_years)
  };
}
